/* eslint-disable no-use-before-define */
/*
 * This file includes helper functions that translates
 * an Allesseh JSON query string <=> QueryRules format
 * in order to more easily manage the UI display.
 */

import { QueryRuleFormKeyConfig } from 'components/query-rules-form/components/query-rule-form/query-rule-configs/types';
import {
  AllessehContentQueryBody,
  AllessehQueryRule,
  AllessehQuerySortKeyTypes,
  AllessehQuerySortOrderType,
  AllessehQuerySortRule
} from 'hooks/useAllessehContentQuery';
import { getOperatorsByType, QueryRuleArrayNameType, QueryRuleOperatorType } from './queryRulesFormEnums';

export { QueryRuleArrayNameType, QueryRuleOperatorType, getOperatorsByType };

export type QueryRuleFlags = { [K in keyof QueryRules]?: boolean } | QueryRules;
export type QueryRuleFilterKey = { [key in QueryRuleArrayNameType]?: string[] };

export interface QueryRule {
  key: string;
  operator: QueryRuleOperatorType;
  value: string;
}

export interface QueryRules {
  all: QueryRule[];
  any: QueryRule[];
  not: QueryRule[];
  sort: AllessehQuerySortRule[];
}

export const DEFAULT_COUNT = 10;

export const DEFAULT_QUERY_RULES: QueryRules = {
  all: [],
  any: [],
  not: [],
  sort: []
};

export const DEFAULT_SORT_RULE: AllessehQuerySortRule = {
  key: AllessehQuerySortKeyTypes.LiveDate,
  order: AllessehQuerySortOrderType.DESC
};

const getQueryRuleConfig = (
  _publicationKey: string | null,
  fetchedSchema?: Record<string, unknown> | null
): Record<string, QueryRuleFormKeyConfig> => (fetchedSchema ?? {}) as Record<string, QueryRuleFormKeyConfig>;

export const getQueryRuleByArrayName = (
  publicationKey: string | null,
  queryRuleFilterKey?: string[],
  fetchedSchema?: Record<string, unknown> | null
) => {
  const queryConfig = getQueryRuleConfig(publicationKey, fetchedSchema);

  if (!queryRuleFilterKey) {
    return queryConfig;
  }

  // Single-pass build of the filtered config — avoids spreading on every iteration.
  const filtered: Record<string, QueryRuleFormKeyConfig> = {};
  queryRuleFilterKey.forEach((key) => {
    if (key in queryConfig) {
      filtered[key] = queryConfig[key];
    }
  });
  return filtered;
};

export const getDefaultQueryRule = (
  publicationKey: string | null,
  arrayName: QueryRuleArrayNameType,
  queryRuleFilterKey?: QueryRuleFilterKey,
  fetchedSchema?: Record<string, unknown> | null
): QueryRule => {
  const queryConfig = getQueryRuleByArrayName(publicationKey, queryRuleFilterKey?.[arrayName], fetchedSchema);

  let key = 'SectionType';

  if (!(key in queryConfig)) {
    const rulesAllowed = Object.keys(queryConfig);

    if (rulesAllowed.length > 0) {
      [key] = rulesAllowed;
    }
  }

  // queryConfig can be empty if the filter list contains only keys not in
  // the resolved config — index lookups return undefined at runtime even
  // though TS's Record<string, …> doesn't model that. Cast so the optional
  // chain below is recognized as needed and we don't crash on an empty
  // config.
  const entry = queryConfig[key] as QueryRuleFormKeyConfig | undefined;

  return {
    key,
    value: '',
    operator: getOperatorsByType(arrayName, entry?.queryRuleFormType)[0]
  };
};

export const getQueryRulesFromAllessehQueryTerms = (jsonQuery?: string | null): QueryRules => {
  const all: QueryRule[] = [];
  const any: QueryRule[] = [];
  const not: QueryRule[] = [];

  const parsedJson = jsonQuery ? (JSON.parse(jsonQuery) as AllessehContentQueryBody) : {};
  if (parsedJson.query) {
    if (parsedJson.query.and) {
      parsedJson.query.and.forEach((andRule: AllessehQueryRule) => {
        const queryRule = getQueryRuleFromAllessehQueryTerm(andRule, QueryRuleOperatorType.EQUAL);
        if (queryRule) {
          all.push(queryRule);
        }
      });
    }
    if (parsedJson.query.or) {
      parsedJson.query.or.forEach((orRule: AllessehQueryRule) => {
        const queryRule = getQueryRuleFromAllessehQueryTerm(orRule, QueryRuleOperatorType.EQUAL);
        if (queryRule) {
          any.push(queryRule);
        }
      });
    }
    if (parsedJson.query.not) {
      parsedJson.query.not.forEach((notRule: AllessehQueryRule) => {
        const queryRule = getQueryRuleFromAllessehQueryTerm(notRule, QueryRuleOperatorType.NOT_EQUAL);
        if (queryRule) {
          not.push(queryRule);
        }
      });
    }
  }

  return { all, any, not, sort: parsedJson.sort ?? [] };
};

export const getAllessehQueryTermsFromQueryRules = (
  queryRules: QueryRules | null,
  schema?: Record<string, unknown> | null
) => {
  const and: AllessehQueryRule[] = [];
  const or: AllessehQueryRule[] = [];
  const not: AllessehQueryRule[] = [];

  if (!queryRules) {
    return '';
  }

  queryRules.all.forEach((allRule) => {
    const allessehTerm = getAllessehQueryTermsFromQueryRule(allRule, schema);
    if (allessehTerm) {
      and.push(allessehTerm);
    }
  });

  queryRules.any.forEach((anyRule) => {
    const allessehTerm = getAllessehQueryTermsFromQueryRule(anyRule, schema);
    if (allessehTerm) {
      or.push(allessehTerm);
    }
  });

  queryRules.not.forEach((anyRule) => {
    const allessehTerm = getAllessehQueryTermsFromQueryRule(anyRule, schema);
    if (allessehTerm) {
      not.push(allessehTerm);
    }
  });

  return JSON.stringify({
    query: { and, or, not },
    sort: queryRules.sort
  });
};

const getQueryRuleFromAllessehQueryTerm = (
  queryRule: AllessehQueryRule,
  defaultOperator: QueryRuleOperatorType
): QueryRule | null => {
  // TODO: support query, numeric_term, prefix, ticker, code_with_significance
  if ('date' in queryRule) {
    return {
      key: queryRule.date.key,
      value: queryRule.date.value,
      operator: getDateQueryRuleOperatorTypeFromAllessehOperand(queryRule.date.operand)
    };
  }
  if ('group' in queryRule) {
    return {
      key: 'Group',
      value: queryRule.group.name,
      operator: defaultOperator
    };
  }
  if ('term' in queryRule) {
    return {
      key: queryRule.term.key,
      value: queryRule.term.value,
      operator: defaultOperator
    };
  }
  if ('terms' in queryRule) {
    return {
      key: queryRule.terms.key,
      value: queryRule.terms.value.join(','),
      operator: defaultOperator
    };
  }
  console.warn('Could not convert query term for ', queryRule);
  return null;
};

const getDateQueryRuleOperatorTypeFromAllessehOperand = (operand: string) => {
  switch (operand) {
    case 'Greater':
      return QueryRuleOperatorType.GREATER_THAN;
    case 'Less':
      return QueryRuleOperatorType.LESS_THAN;
    case 'GreaterEquals':
      return QueryRuleOperatorType.GREATER_THAN_EQUAL;
    case 'LessEquals':
      return QueryRuleOperatorType.LESS_THAN_EQUAL;
    case 'Equals':
    default:
      return QueryRuleOperatorType.EQUAL;
  }
};

const getAllessehOperandFromDateQueryRuleOperatorType = (operand: QueryRuleOperatorType) => {
  switch (operand) {
    case QueryRuleOperatorType.GREATER_THAN:
      return 'Greater';
    case QueryRuleOperatorType.LESS_THAN:
      return 'Less';
    case QueryRuleOperatorType.GREATER_THAN_EQUAL:
      return 'GreaterEquals';
    case QueryRuleOperatorType.LESS_THAN_EQUAL:
      return 'LessEquals';
    case QueryRuleOperatorType.EQUAL:
    default:
      return 'Equals';
  }
};

const termOrTerms = (rule: QueryRule): AllessehQueryRule =>
  rule.value.length > 0 && rule.value.includes(',')
    ? { terms: { key: rule.key, value: rule.value.split(',') } }
    : { term: { key: rule.key, value: rule.value } };

const getAllessehQueryTermsFromQueryRule = (
  rule: QueryRule,
  schema?: Record<string, unknown> | null
): AllessehQueryRule | null => {
  const entry = schema?.[rule.key] as { queryRuleFormType?: string } | undefined;
  const formType = entry?.queryRuleFormType;
  if (formType === 'date') {
    return {
      date: {
        key: rule.key,
        value: rule.value,
        operand: getAllessehOperandFromDateQueryRuleOperatorType(rule.operator)
      }
    };
  }
  if (formType === 'group') {
    return { group: { name: rule.value } };
  }
  return termOrTerms(rule);
};
